import { useEffect, useState } from "react";
import { UserPlus, Trash2, Edit3 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

export default function AdminPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", password: "" });
  const [editing, setEditing] = useState(null); // user obj
  const [editForm, setEditForm] = useState({ username: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setEmployees(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password) {
      toast.error("الرجاء إدخال اسم المستخدم وكلمة المرور");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("تم إضافة الموظف");
      setOpen(false);
      setForm({ username: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const update = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const payload = {};
    if (editForm.username.trim() && editForm.username.trim() !== editing.username) payload.username = editForm.username.trim();
    if (editForm.password) payload.password = editForm.password;
    if (Object.keys(payload).length === 0) {
      toast.info("لا توجد تغييرات");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/users/${editing.id}`, payload);
      toast.success("تم التحديث");
      setEditing(null);
      setEditForm({ username: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الموظف؟")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="text-4xl font-display font-extrabold text-zinc-900 tracking-tight">إدارة الموظفين</h1>
          <p className="text-sm text-zinc-600 mt-1">يمكنك إضافة حتى 3 موظفين والتحكم بحساباتهم</p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="add-employee-btn"
          disabled={employees.length >= 3}
          className="bg-teal-700 hover:bg-teal-800 font-bold"
        >
          <UserPlus className="h-4 w-4 ms-1" />
          إضافة موظف
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl elevated-card overflow-hidden" data-testid="employees-list">
        {loading ? (
          <div className="p-12 text-center text-zinc-400">جاري التحميل...</div>
        ) : employees.length === 0 ? (
          <div className="p-12 text-center text-zinc-400">لا يوجد موظفون — أضف أول موظف</div>
        ) : (
          <ul>
            {employees.map((u) => (
              <li key={u.id} className="px-6 py-4 border-b last:border-b-0 border-zinc-100 flex items-center justify-between" data-testid={`employee-row-${u.username}`}>
                <div>
                  <div className="text-base font-semibold text-zinc-900">{u.username}</div>
                  <div className="text-xs text-zinc-500">موظف</div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setEditing(u); setEditForm({ username: u.username, password: "" }); }}
                    data-testid={`edit-employee-btn-${u.username}`}
                    className="text-zinc-500 hover:text-teal-700 hover:bg-teal-50 p-2 rounded-lg transition-colors"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => remove(u.id)}
                    data-testid={`delete-employee-btn-${u.username}`}
                    className="text-zinc-400 hover:text-red-600 p-2 rounded transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle>إضافة موظف</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label htmlFor="eu">اسم المستخدم</Label>
              <Input id="eu" data-testid="new-employee-username-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="ep">كلمة المرور</Label>
              <Input id="ep" type="password" data-testid="new-employee-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
              <Button type="submit" disabled={saving} data-testid="new-employee-save-btn" className="bg-teal-700 hover:bg-teal-800 font-bold">
                {saving ? "جاري..." : "حفظ"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle>تعديل موظف</DialogTitle>
          </DialogHeader>
          <form onSubmit={update} className="space-y-4">
            <div>
              <Label htmlFor="eu2">اسم المستخدم</Label>
              <Input id="eu2" data-testid="edit-employee-username-input" value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="ep2">كلمة مرور جديدة (اختياري)</Label>
              <Input id="ep2" type="password" data-testid="edit-employee-password-input" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="اتركها فارغة للإبقاء على الحالية" className="mt-1" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>إلغاء</Button>
              <Button type="submit" disabled={saving} data-testid="edit-employee-save-btn" className="bg-teal-700 hover:bg-teal-800 font-bold">
                {saving ? "جاري..." : "حفظ"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
